import { AppLinkTokenResponse } from 'src/app/core/models/AppLinkTokenResponse';
import { Component, OnInit, OnChanges, Input, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { MembershipService } from 'src/app/membership/membership/membership.service';
import { StoreMode } from 'src/app/core/enums';
import { UserService } from 'src/app/core/user/user.service';
import { MembershipWithPointStampResponse } from 'src/app/membership/membership/membership-with-point-stamp-response';
import { MembershipRewardType } from 'src/app/membership/membership/RewardType';
import { StoreService } from 'src/app/store/store/store.service';
import { StoreResponse } from 'src/app/core/models/StoreResponse';
import { ExternalMemberLinkMode } from 'src/app/membership/membership/external-member-link-mode.enum';
import { MembershipDetailResponse } from 'src/app/membership/membership/membership-detail-response';
import { SessionStorageService } from 'src/app/shared/storage/session-storage.service';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { ExternalLoginUrlRequest } from 'src/app/core/models/LoginRequest';
import { PopupMessageService } from 'src/app/core/services/popup-message.service';
import { SubscriptionTagConfigService } from 'src/app/core/services/subscription-tag-config.service';
import { ChannelService } from 'src/app/home/channel/channel.service';

export interface MembershipCardAction {
  type: 'login' | 'external-login' | 'join';
  membership: MembershipWithPointStampResponse;
}

@Component({
  selector: 'app-membership-card',
  templateUrl: './membership-card.component.html',
  styleUrls: ['./membership-card.component.scss']
})
export class MembershipCardComponent implements OnInit, OnChanges {
  @Input() membership: MembershipWithPointStampResponse[];
  @Input() appLinkTokenResponse: AppLinkTokenResponse;
  @Input() storeResponse: StoreResponse;
  @Input() isStorePage: boolean = false;
  @Input() isRewardPage: boolean = false;
  @Input() isHomePage: boolean = false;
  @Input() isDashboardMembership: boolean = false;
  @Output() membershipAction = new EventEmitter<MembershipCardAction>();

  user$: Subscription;
  isLoggedIn: boolean = false;
  cardImageError: boolean = false;
  showSRBanner: boolean = false;
  checkRewardType = MembershipRewardType;

  show: boolean = false;
  displayExternalMembershipLoginDialog: boolean = false;

  externalMemberDataSub$: Subscription;
  externalMemberResponse: MembershipDetailResponse;
  displayedMembership: MembershipWithPointStampResponse[] = [];
  loginByMobileNo = ExternalMemberLinkMode.LOGINBYMOBILENO;
  loginByRedirect = ExternalMemberLinkMode.LOGINBYREDIRECT;

  constructor(
    private membershipService: MembershipService,
    private userService: UserService,
    private storeService: StoreService,
    private sessionStorageService: SessionStorageService,
    private navigationService: NavigationService,
    private popupMessageService: PopupMessageService,
    private subscriptionTagConfigService: SubscriptionTagConfigService,
    private channelService: ChannelService,
  ) { }

  async ngOnInit() {
    this.user$ = this.userService.get(StoreMode.Internal).subscribe((userData: any) => {
      this.isLoggedIn = userData && userData.id != 999 ? true : false;
    });

    if (!this.useInputMembershipOnly) {
      this.externalMemberDataSub$ = this.membershipService.getExternalMembershipResponseObservable().subscribe(response => {
        this.externalMemberResponse = response;
        this.updateDisplayedMembership();
      });
      // A redirect/reload can restore the login from session storage before the query is hydrated.
      this.externalMemberResponse = this.externalMemberResponse ??
        this.membershipService.getExternalMemberData()?.membershipDetailResponse;
    }
    this.updateDisplayedMembership();

    this.showSRBanner = await this.subscriptionTagConfigService.shouldShowPDPA(this.channelService.getSubscriptionTag());
  }

  ngOnChanges(): void {
    this.updateDisplayedMembership();
  }

  onImgError() {
    this.cardImageError = true;
  }

  async onClickMember(memberData: MembershipWithPointStampResponse) {
    if (!memberData) return;

    if (this.isRewardPage) {
      await this.onClickStore(memberData);
    } else {
      if (this.isStorePage) {
        this.sessionStorageService.setItem('isFromPage', 'store');
        await this.storeInfoProcess();
      }

      // External login does not sign in the e-Order user. Check the matching
      // external membership before prompting for an internal account login.
      if (!this.useInputMembershipOnly) {
        this.externalMemberResponse = this.membershipService.getExternalMemberData()?.membershipDetailResponse;
        this.updateDisplayedMembership();
        const externalMember = this.getExternalMembership(memberData);
        if (externalMember) {
          const membershipDesc = externalMember.membershipDesc ?? memberData.membershipDesc;
          const membershipTag = await this.storeService.replaceWhiteSpaceWithDash(membershipDesc);
          this.navigationService.navigate(['membership', memberData.membershipCode, membershipTag]);
          return;
        }
      }

      if (!this.isLoggedIn && !this.appLinkTokenResponse && ![this.loginByMobileNo, this.loginByRedirect].includes(memberData.externalMemberLinkMode)) {
        this.requestMembershipAction('login', memberData);
        return;
      }

      if (!memberData.membershipNo) {
        if (!this.appLinkTokenResponse && memberData.externalMemberLinkMode === ExternalMemberLinkMode.LOGINBYMOBILENO) {
          this.requestMembershipAction('external-login', memberData);
          return;
        }

        if (!this.appLinkTokenResponse && memberData.externalMemberLinkMode === ExternalMemberLinkMode.LOGINBYREDIRECT) {
          let loginReq = {} as ExternalLoginUrlRequest;
          loginReq.membershipCode = memberData.membershipCode;
          loginReq.callbackUrl = window.location.href;

          localStorage.setItem('membershipcode', memberData.membershipCode);

          let response = await this.membershipService.getmembershipbyloginURL(loginReq);

          if (response && response.success && response.loginUrl) {
            localStorage.setItem('redirectURL', loginReq.callbackUrl);
            window.location.href = response.loginUrl;
          } else {
            localStorage.removeItem('membershipcode')
            this.membershipService.removeExternalMembershipData(memberData.membershipCode);
            let errorMessage = response?.errorMessage ?? 'An error occurred';
            this.popupMessageService.show({
              icon: "icon-alert",
              title: "something.went.wrong",
              desc: errorMessage,
              btn: "button.retry",
            });
          }
          return;
        }
      }

      if (memberData.membershipId || memberData.membershipNo) {
        let locDescWithoutSpaces = await this.storeService.replaceWhiteSpaceWithDash(memberData.membershipDesc);
        this.navigationService.navigate(['membership', memberData.membershipCode, locDescWithoutSpaces]);
      }
      else if (!memberData.membershipId && !memberData.membershipNo && ![this.loginByMobileNo, this.loginByRedirect].includes(memberData.externalMemberLinkMode)) {
        this.requestMembershipAction('join', memberData);
      }
    }
  }

  async onClickStore(memberData: MembershipWithPointStampResponse) {
    this.sessionStorageService.setItem('isFromPage', 'reward');

    let membershipDescWithoutSpaces = await this.storeService.replaceWhiteSpaceWithDash(memberData.membershipDesc)
    this.navigationService.navigate(['membership', memberData.membershipCode, membershipDescWithoutSpaces]);
  }

  private requestMembershipAction(type: MembershipCardAction['type'], membership: MembershipWithPointStampResponse): void {
    if (this.isDashboardMembership || this.isHomePage) {
      this.membershipAction.emit({ type, membership });
      return;
    }
    if (type === 'login') this.membershipService.loginDialog.next(true);
    else if (type === 'external-login') this.membershipService.externalMembershipLogin.next(true);
    else this.membershipService.joinTermDialog.next(true);
  }

  async onClickNav() {
    this.sessionStorageService.setItem('isFromPage', 'store');

    await this.storeInfoProcess();

    let locDescWithoutSpaces = await this.storeService.replaceWhiteSpaceWithDash(this.externalMemberResponse.membershipDesc);
    this.navigationService.navigate(['membership', this.externalMemberResponse.membershipCode, locDescWithoutSpaces]);
  }

  async storeInfoProcess() {
    let storeInfo = {} as Partial<StoreResponse>
    storeInfo.storeId = this.storeResponse.storeId;
    storeInfo.locDesc = this.storeResponse.locDesc;
    storeInfo.currentOrderType = this.storeResponse.currentOrderType;
    this.storeService.saveMembershipStoreInfo(storeInfo);
  }

  private getExternalMembership(member: MembershipWithPointStampResponse): MembershipDetailResponse | null {
    const externalMember = this.externalMemberResponse;
    return member.membershipCode && externalMember?.membershipNo &&
      externalMember.membershipCode === member.membershipCode ? externalMember : null;
  }

  private updateDisplayedMembership(): void {
    const memberships = (this.membership ?? []).filter(member => !!member);
    if (this.useInputMembershipOnly) {
      this.displayedMembership = memberships;
      return;
    }

    const visibleMemberships = this.isStorePage ? memberships.slice(0, 1) : memberships;
    this.displayedMembership = visibleMemberships.map(member => {
      const externalMember = this.getExternalMembership(member);
      return externalMember ? {
        ...member,
        membershipNo: externalMember.membershipNo,
        membershipId: externalMember.membershipId ?? member.membershipId,
        membershipDesc: externalMember.membershipDesc ?? member.membershipDesc,
        cardImageData: externalMember.cardImageData ?? member.cardImageData,
        logoImageData: externalMember.logoImageData ?? member.logoImageData,
        rewardType: externalMember.rewardType ?? member.rewardType,
        closingPoint: externalMember.closingPoint ?? member.closingPoint,
        stampEarned: externalMember.stampEarned ?? member.stampEarned,
        totalStamp: externalMember.totalStamp ?? member.totalStamp,
        levelDesc: externalMember.levelDesc ?? member.levelDesc,
        expiryDate: externalMember.expiryDate ?? member.expiryDate,
        membershipLevel: externalMember.membershiplevel ?? member.membershipLevel,
        externalMemberLinkMode: externalMember.externalMemberLinkMode ?? member.externalMemberLinkMode,
      } : member;
    });
  }


  ngOnDestroy(): void {
    this.user$?.unsubscribe();
    this.externalMemberDataSub$?.unsubscribe();
  }

  private get useInputMembershipOnly(): boolean {
    return this.isHomePage || this.isDashboardMembership;
  }

  isExpired(expiryDate?: string): boolean {
    return this.membershipService.isExpired(expiryDate);
  }

}
